import { Check, Minus } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { Reveal } from "@/components/site/Reveal";
import { pricingComparison, pricingPlans, type ComparisonValue } from "@/lib/finendra-data";

function Cell({ value, plan }: { value: ComparisonValue; plan: string }) {
  if (value === true) {
    return (
      <span className="inline-flex items-center justify-center">
        <Check className="h-5 w-5 text-brand-accent" aria-hidden="true" />
        <span className="sr-only">Included in {plan}</span>
      </span>
    );
  }
  if (value === false) {
    return (
      <span className="inline-flex items-center justify-center">
        <Minus className="h-5 w-5 text-brand-navy/25" aria-hidden="true" />
        <span className="sr-only">Not included in {plan}</span>
      </span>
    );
  }
  return <span className="text-sm font-semibold text-brand-navy/80">{value}</span>;
}

export function PricingComparison() {
  return (
    <div className="mx-auto max-w-6xl">
      <Reveal>
        <div className="max-w-2xl">
          <p className="text-xs font-bold uppercase tracking-widest text-brand-accent">
            Compare plans
          </p>
          <h2 className="mt-4 font-display text-3xl font-bold text-brand-navy sm:text-4xl">
            What's included in every tier
          </h2>
          <p className="mt-5 text-lg leading-relaxed text-brand-navy/60">
            A line-by-line breakdown of the scope, seniority, and deliverables behind each plan — so
            you can match the role to the work you actually need done.
          </p>
        </div>
      </Reveal>

      <Reveal delay={0.1}>
        <p className="mt-8 text-sm text-brand-navy/50 lg:hidden">
          Scroll the table sideways to compare all four plans.
        </p>

        <div className="mt-6 overflow-x-auto rounded-3xl border border-brand-navy/10 bg-background shadow-xs">
          <table className="w-full min-w-[52rem] border-collapse text-left">
            <caption className="sr-only">
              Detailed comparison of Finendra accounting plans by scope and deliverables
            </caption>
            <thead>
              <tr className="border-b border-brand-navy/10">
                <th
                  scope="col"
                  className="sticky left-0 z-10 bg-background/95 backdrop-blur-md backdrop-saturate-150 px-6 py-6 text-xs font-bold uppercase tracking-wider text-brand-navy/50 shadow-[inset_-1px_0_0_rgba(15,23,42,0.06)]"
                >
                  Capability
                </th>
                {pricingPlans.map((plan) => (
                  <th
                    key={plan.name}
                    scope="col"
                    className={`px-6 py-6 text-center align-bottom ${
                      plan.featured ? "bg-brand-surface" : ""
                    }`}
                  >
                    <span className="block text-[0.65rem] font-bold uppercase tracking-wider text-brand-accent">
                      {plan.badge}
                    </span>
                    <span className="mt-2 block font-display text-lg font-bold tracking-tight text-brand-navy">
                      {plan.name}
                    </span>
                    <span className="mt-1 block text-sm font-semibold tracking-tight text-brand-navy/70">
                      {plan.price !== null && plan.price !== undefined
                        ? `$${plan.price.toLocaleString("en-US")} /mo`
                        : "Custom"}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>

            {pricingComparison.map((group) => (
              <tbody key={group.title}>
                <tr className="bg-brand-surface/60">
                  <th
                    scope="colgroup"
                    colSpan={pricingPlans.length + 1}
                    className="px-6 py-3 text-xs font-bold uppercase tracking-wider text-brand-navy"
                  >
                    {group.title}
                  </th>
                </tr>
                {group.rows.map((row) => (
                  <tr
                    key={row.label}
                    className="border-t border-brand-navy/5 transition-colors hover:bg-brand-surface/40"
                  >
                    <th
                      scope="row"
                      className="sticky left-0 z-10 bg-background/95 backdrop-blur-md backdrop-saturate-150 px-6 py-4 text-sm font-semibold text-brand-navy shadow-[inset_-1px_0_0_rgba(15,23,42,0.06)]"
                    >
                      {row.label}
                      {row.hint && (
                        <span className="mt-1 block text-xs font-normal text-brand-navy/45">
                          {row.hint}
                        </span>
                      )}
                    </th>
                    {row.values.map((value, index) => {
                      const plan = pricingPlans[index];
                      return (
                        <td
                          key={plan?.name ?? index}
                          className={`px-6 py-4 text-center ${
                            plan?.featured ? "bg-brand-surface/70" : ""
                          }`}
                        >
                          <Cell value={value} plan={plan?.name ?? "this plan"} />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            ))}

            <tfoot>
              <tr className="border-t border-brand-navy/10">
                <td className="sticky left-0 z-10 bg-background/95 backdrop-blur-md backdrop-saturate-150 px-6 py-6 shadow-[inset_-1px_0_0_rgba(15,23,42,0.06)]" />
                {pricingPlans.map((plan) => (
                  <td
                    key={plan.name}
                    className={`px-6 py-6 text-center ${plan.featured ? "bg-brand-surface" : ""}`}
                  >
                    <Link
                      to="/contact"
                      className={`inline-flex rounded-xl px-5 py-3 text-xs font-bold transition-[background-color,border-color,color,transform] duration-150 ease-out active:scale-[0.975] active:duration-75 motion-reduce:transform-none select-none ${
                        plan.featured
                          ? "bg-brand-navy text-background hover:bg-brand-accent shadow-xs active:bg-brand-navy/90"
                          : "border border-brand-navy/15 text-brand-navy hover:border-brand-accent hover:text-brand-accent active:bg-brand-surface"
                      }`}
                    >
                      {plan.cta}
                    </Link>
                  </td>
                ))}
              </tr>
            </tfoot>
          </table>
        </div>
      </Reveal>
    </div>
  );
}
